import { Label } from "@/components/ui/label";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PlacementAvailability } from "@/lib/publicAvailability";
import { getPlacementHelpMessage, getPublicAvailabilityLabel } from "@/lib/publicAvailability";

export interface BookableZoneOption {
  id: string;
  name: string;
  color?: string | null;
}

interface ZonePreferenceSelectorProps {
  zones: BookableZoneOption[];
  value: string[]; // selected zone IDs
  onChange: (value: string[]) => void;
  mode: 'single' | 'multi';
  helpText?: string;
  compact?: boolean;
  /** When true, displays an "Aucune préférence" option that clears the selection */
  allowNoPreference?: boolean;
  label?: string;
  availability?: Record<string, PlacementAvailability>;
}

/**
 * Strict zone preference selector — works for any number of named zones.
 * - mode='single': behaves like a radio group (one zone, or "no preference" if allowed)
 * - mode='multi':  behaves like a checkbox group (multiple zones)
 *
 * Empty array = "no preference" (no filter applied server-side).
 */
export function ZonePreferenceSelector({
  zones,
  value,
  onChange,
  mode,
  helpText,
  compact = false,
  allowNoPreference = true,
  label = "Zone souhaitée",
  availability,
}: ZonePreferenceSelectorProps) {
  if (!zones || zones.length === 0) return null;

  const isSelected = (id: string) => value.includes(id);
  const noneSelected = value.length === 0;

  const handleToggle = (id: string) => {
    if (mode === 'single') {
      onChange(isSelected(id) && allowNoPreference ? [] : [id]);
    } else {
      if (isSelected(id)) {
        onChange(value.filter(v => v !== id));
      } else {
        onChange([...value, id]);
      }
    }
  };

  const handleNoPreference = () => onChange([]);
  const unavailableSelectedZone = value.find(id => availability?.[id]?.status !== undefined && availability[id].status !== 'available');
  const selectedMessage = unavailableSelectedZone
    ? getPlacementHelpMessage(`dans ${zones.find(zone => zone.id === unavailableSelectedZone)?.name ?? 'cette zone'}`, availability?.[unavailableSelectedZone])
    : undefined;

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <div className="flex items-center gap-2">
        <MapPin className={compact ? "w-4 h-4 text-primary" : "w-5 h-5 text-primary"} />
        <Label className={compact ? "text-sm font-semibold" : "text-lg font-semibold"}>
          {label}
        </Label>
      </div>
      {helpText && (
        <p className={compact ? "text-xs text-muted-foreground whitespace-pre-line" : "text-sm text-muted-foreground whitespace-pre-line"}>
          {helpText}
        </p>
      )}
      <div className="flex flex-wrap gap-2" role={mode === 'single' ? 'radiogroup' : 'group'}>
        {allowNoPreference && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNoPreference}
            className={`h-auto flex items-center gap-2 px-3 py-2 border-2 ${
              noneSelected
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50'
            }`}
            aria-pressed={noneSelected}
          >
            <span className={compact ? "text-xs" : "text-sm"}>Sans préférence</span>
          </Button>
        )}
        {zones.map(zone => {
          const selected = isSelected(zone.id);
          const zoneAvailability = availability?.[zone.id];
          const unavailable = zoneAvailability !== undefined && zoneAvailability.status !== 'available';
          return (
            <Button
              key={zone.id}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleToggle(zone.id)}
              disabled={unavailable}
              className={`h-auto flex items-center gap-2 px-3 py-2 border-2 ${
                selected
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50'
              }`}
              aria-pressed={selected}
            >
              {zone.color && (
                <span
                  className="inline-block w-3 h-3 rounded-full border border-border"
                  style={{ backgroundColor: zone.color }}
                  aria-hidden
                />
              )}
              <span className={compact ? "text-xs" : "text-sm"}>{zone.name}</span>
              {unavailable && <span className="text-xs text-muted-foreground">{getPublicAvailabilityLabel(zoneAvailability.status)}</span>}
            </Button>
          );
        })}
      </div>
      {selectedMessage && <p role="status" className="text-sm text-destructive">{selectedMessage}</p>}
    </div>
  );
}
